import React from "react";
import Dialog from "@mui/material/Dialog";
import { Box, IconButton, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Warning, Error, Info, CheckCircle, Close } from "@mui/icons-material";
import Button from "./Button";
import { useModalStyles } from "./useModalStyles";

export type WarningPopupVariant = "warning" | "error" | "info" | "success";

export interface WarningPopupButton {
  name: string;
  color: "primary" | "secondary" | "success" | "error" | "info" | "warning";
  onClick: (obj?: any) => void;
  variant?: "contained" | "outlined" | "text";
  icon?: React.ReactNode;
}

export type WarningPopupObjectProps = {
  show: boolean;
  message?: React.ReactNode;
  variant?: WarningPopupVariant;
  buttons?: WarningPopupButton[];
  title?: string;
  icon?: React.ReactNode;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
  showCloseIcon?: boolean;
  customBackground?: string;
  obj?: any;
};

export interface WarningPopupProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  onSubmit?: (obj?: any) => void;
  message?: React.ReactNode;
  variant?: WarningPopupVariant;
  buttons?: WarningPopupButton[];
  icon?: React.ReactNode;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
  showCloseIcon?: boolean;
  customBackground?: string;
  obj?: any;
}

const WarningPopup: React.FC<WarningPopupProps> = ({
  open,
  onClose,
  title = "Warning",
  message = "Please review the information below.",
  variant = "warning",
  buttons = [{ name: "OK", color: "primary", onClick: onClose }],
  icon,
  maxWidth = "sm",
  showCloseIcon = false,
  customBackground,
  obj,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    tokens,
    paperSx,
    headerSx,
    bodySx,
    footerSx,
    closeIconSx,
    primaryBtnSx,
    cancelBtnSx,
    destructiveBtnSx,
  } = useModalStyles();

  const isDarkMode = theme.palette.mode === "dark";

  // Get variant-based configurations
  const getVariantConfig = (variant: WarningPopupVariant) => {
    switch (variant) {
      case "error":
        return {
          icon: <Error sx={{ fontSize: "3.5rem", color: "#EF4444" }} />,
          titleColor: "#EF4444",
          background: isDarkMode ? "rgba(30, 18, 18, 0.8)" : "#FEF2F2",
        };
      case "success":
        return {
          icon: <CheckCircle sx={{ fontSize: "3.5rem", color: "#22C55E" }} />,
          titleColor: "#22C55E",
          background: isDarkMode ? "rgba(13, 41, 18, 0.8)" : "#ECFDF5",
        };
      case "info":
        return {
          icon: <Info sx={{ fontSize: "3.5rem", color: "#3B82F6" }} />,
          titleColor: "#3B82F6",
          background: isDarkMode ? "rgba(16, 24, 40, 0.85)" : "#EFF6FF",
        };
      default:
        return {
          icon: <Warning sx={{ fontSize: "3.5rem", color: "#FACC15" }} />,
          titleColor: "#FACC15",
          background: isDarkMode ? "rgba(31, 27, 8, 0.85)" : "#FEF3C7",
        };
    }
  };

  const config = getVariantConfig(variant);

  const getButtonSx = (button: WarningPopupButton) => {
    const variantType = button.variant ?? (button.name.toLowerCase().includes("cancel") ? "outlined" : "contained");
    if (button.color === "error" || variantType === "outlined") {
      return cancelBtnSx;
    }
    if (button.color === "secondary" || button.color === "warning") {
      return destructiveBtnSx;
    }
    return primaryBtnSx;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: paperSx(isMobile) }}
    >
      <Box sx={{ backgroundColor: customBackground || tokens.bg }}>
        <Box
          sx={{
            ...headerSx,
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            flexDirection: "column",
            textAlign: "center",
            gap: 1.5,
          }}
        >
          {/* FIX: Removed the container background styles wrapper completely.
            The raw structural symbol icon now sits cleanly on its own layout surface.
          */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: 0.5 }}>
            {icon || config.icon}
          </Box>

          <Typography
            fontWeight={700}
            sx={{ fontSize: "1.2rem", color: tokens.textPrimary, lineHeight: 1.3 }}
          >
            {title}
          </Typography>
          <Typography sx={{ fontSize: "0.85rem", color: tokens.textSecondary, mt: -0.5 }}>
            {variant === "warning" ? "Proceed with caution." : "Please review before continuing."}
          </Typography>
          {showCloseIcon && (
            <IconButton
              onClick={onClose}
              size="small"
              sx={{ ...closeIconSx, position: "absolute", right: 16, top: 16 }}
            >
              <Close sx={{ fontSize: "1rem" }} />
            </IconButton>
          )}
        </Box>

        <Box
          sx={{
            ...bodySx,
            py: 3,
            textAlign: "center",
            color: tokens.textPrimary,
            backgroundColor: tokens.bg,
          }}
        >
          <Typography
            component="div"
            sx={{
              fontSize: "0.95rem",
              lineHeight: 1.8,
              fontWeight: 500,
              color: tokens.textSecondary,
              wordBreak: "break-word",
              "& p": { m: 0 },
            }}
          >
            {message}
          </Typography>
        </Box>

        <Box sx={footerSx}>
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            {[...buttons].reverse().map((button, index) => (
              <Button
                key={index}
                fullWidth
                variant={button.variant ?? (button.name.toLowerCase().includes("cancel") ? "outlined" : "contained")}
                color={button.name.toLowerCase().includes("cancel") ? "error" : button.color}
                onClick={() => button.onClick(obj)}
                startIcon={button.icon}
                sx={getButtonSx(button)}
              >
                {button.name}
              </Button>
            ))}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default WarningPopup;